import { useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { MessengerChat } from "./MessengerChat";
import {
  MessengerChatComposer,
  type ChatComposerAttachment,
} from "./MessengerChatComposer";
import {
  mapSupportTicketMessageToChatMessage,
  type SupportTicketChatPerspective,
  type SupportTicketMessageLike,
} from "./mappers/supportTicket";
import { TicketStatus } from "@/components/tickets";

export type { ChatComposerAttachment as SupportTicketAttachment };

export interface SupportTicketChatProps {
  messages: SupportTicketMessageLike[];
  perspective: SupportTicketChatPerspective;
  status: TicketStatus;
  isSending: boolean;
  onSend: (
    message: string,
    attachments: ChatComposerAttachment[],
    isInternalNote: boolean,
  ) => Promise<void>;
  title: string;
  subtitle?: string;
  avatarInitials?: string;
  showInternalNoteToggle?: boolean;
  sendPlaceholder?: string;
  internalNotePlaceholder?: string;
  closedFooter?: ReactNode;
  onRefresh?: () => void;
  isRefreshing?: boolean;
}

export function SupportTicketChat({
  messages,
  perspective,
  status,
  isSending,
  onSend,
  title,
  subtitle,
  avatarInitials,
  showInternalNoteToggle = false,
  sendPlaceholder,
  internalNotePlaceholder,
  closedFooter,
  onRefresh,
  isRefreshing,
}: SupportTicketChatProps) {
  const { t } = useTranslation("support");

  const labels = useMemo(
    () => ({
      internalNote: t("messageBubble.internalNote"),
      supportTeam: t("messageBubble.supportTeam"),
      you: t("messageBubble.you"),
      sellerFallback: t("messageBubble.sellerFallback"),
    }),
    [t],
  );

  const chatMessages = useMemo(() => {
    const visibleMessages =
      perspective === "seller"
        ? messages.filter((message) => message.visibility !== "ADMIN_ONLY")
        : messages;

    return visibleMessages.map((message) =>
      mapSupportTicketMessageToChatMessage(message, {
        perspective,
        labels,
      }),
    );
  }, [messages, perspective, labels]);

  const isClosed = status === TicketStatus.CLOSED;

  return (
    <MessengerChat
      className="flex-1"
      title={title}
      subtitle={subtitle}
      avatarInitials={avatarInitials}
      messages={chatMessages}
      emptyText={t("detail.conversationEmpty")}
      onRefresh={onRefresh}
      isRefreshing={isRefreshing}
      footer={
        isClosed || closedFooter
          ? closedFooter
            ? { kind: "closed", node: closedFooter }
            : undefined
          : {
              kind: "composer",
              node: (
                <MessengerChatComposer
                  onSend={onSend}
                  isSending={isSending}
                  showInternalNoteToggle={showInternalNoteToggle}
                  placeholder={sendPlaceholder}
                  internalNotePlaceholder={internalNotePlaceholder}
                />
              ),
            }
      }
    />
  );
}
